import { create } from 'zustand';
import type {
  QuoteInput,
  ClientInfo,
  LineItem,
  PartnerTier,
  SupportTier,
  Language,
  Market,
} from '@quote-tool/shared';

function emptyClient(): ClientInfo {
  return {
    company: '',
    attention: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    address: '',
    city: '',
    province: 'QC',
    country: '',
    postalCode: '',
    nic: '',
    taxExempt: false,
  };
}

function emptyQuote(): QuoteInput {
  return {
    repName: '',
    language: 'en',
    market: 'CA',
    client: emptyClient(),
    partnerTier: 'none',
    supportTier: 'standard',
    specialDiscountPct: 0,
    lines: [],
    comments: '',
  };
}

interface QuoteStore {
  quote: QuoteInput;
  lastPickerDc: string;

  setRepName: (v: string) => void;
  setLanguage: (v: Language) => void;
  setMarket: (v: Market) => void;
  setLastPickerDc: (v: string) => void;
  setClient: (patch: Partial<ClientInfo>) => void;
  setPartnerTier: (v: PartnerTier) => void;
  setSupportTier: (v: SupportTier) => void;
  setSpecialDiscountPct: (v: number) => void;

  addLine: (l: LineItem) => void;
  updateLine: (id: string, patch: Partial<LineItem>) => void;
  removeLine: (id: string) => void;
  clearLines: () => void;

  setComments: (v: string) => void;
  /** Replace the whole quote — used when opening a saved deal. */
  loadQuote: (q: QuoteInput) => void;
  /** Replace every line at once, e.g. after re-pricing against a fresh catalog. */
  setLines: (lines: LineItem[]) => void;
  reset: () => void;
}

export const useQuoteStore = create<QuoteStore>((set) => ({
  quote: emptyQuote(),
  lastPickerDc: '',

  setRepName: (repName) =>
    set((s) => ({ quote: { ...s.quote, repName } })),

  setLanguage: (language) =>
    set((s) => ({ quote: { ...s.quote, language } })),

  setMarket: (market) =>
    set((s) => ({ quote: { ...s.quote, market } })),

  setLastPickerDc: (lastPickerDc) =>
    set({ lastPickerDc }),

  setClient: (patch) =>
    set((s) => ({ quote: { ...s.quote, client: { ...s.quote.client, ...patch } } })),

  setPartnerTier: (partnerTier) =>
    set((s) => ({ quote: { ...s.quote, partnerTier } })),

  setSupportTier: (supportTier) =>
    set((s) => ({ quote: { ...s.quote, supportTier } })),

  setSpecialDiscountPct: (specialDiscountPct) =>
    set((s) => ({ quote: { ...s.quote, specialDiscountPct } })),

  addLine: (line) =>
    set((s) => ({ quote: { ...s.quote, lines: [...s.quote.lines, line] } })),

  updateLine: (id, patch) =>
    set((s) => ({
      quote: {
        ...s.quote,
        lines: s.quote.lines.map((l) => (l.id === id ? { ...l, ...patch } : l)),
      },
    })),

  removeLine: (id) =>
    set((s) => ({ quote: { ...s.quote, lines: s.quote.lines.filter((l) => l.id !== id) } })),

  clearLines: () =>
    set((s) => ({ quote: { ...s.quote, lines: [] } })),

  setComments: (comments) =>
    set((s) => ({ quote: { ...s.quote, comments } })),

  loadQuote: (quote) => set({ quote }),

  setLines: (lines) =>
    set((s) => ({ quote: { ...s.quote, lines } })),

  reset: () => set({ quote: emptyQuote() }),
}));
